////////// KEYS //////////

import { useQuery, type UseQueryResult, useMutation, type UseMutationResult, skipToken } from '@tanstack/react-query'

import type { AxiosRequestConfig } from 'axios'
import type { DeviceType } from '~/types/device-type'
import type { IProject, IReport, IReportedComment, IReportedPost } from '~/types/report'

import {
  getReportByEntity,
  getReportsByEntities,
  processBatchReport,
  searchComments,
  searchPosts,
  searchProjects,
} from '~/api/reports'

export const keys = {
  all: ['reports'] as const,
  entities: (params?: AxiosRequestConfig['params']) => [...keys.all, 'entities', params] as const,
  entity: (entityId?: string) => [...keys.all, 'entity', entityId] as const,
  searchComments: (params: { appKey?: string; deviceType?: DeviceType; postId?: string; profileId?: string }) =>
    [...keys.all, 'search', 'comments', params] as const,
  searchPosts: (params: { appKey?: string; deviceType?: DeviceType; profileId?: string }) =>
    [...keys.all, 'search', 'posts', params] as const,
  searchProjects: (params: { appKey?: string; deviceType?: DeviceType; profileId?: string }) =>
    [...keys.all, 'search', 'projects', params] as const,
}

////////// QUERIES //////////

export const useReportsByEntitiesQuery = (
  params?: AxiosRequestConfig['params']
): UseQueryResult<IPaginatedResponse<IReport>> => {
  return useQuery({
    queryFn: () => getReportsByEntities(params),
    queryKey: keys.entities(params),
  })
}

export const useGetReportByEntity = ({
  appKey,
  deviceType,
  entityId,
}: {
  appKey?: string
  deviceType?: DeviceType
  entityId?: string
}): UseQueryResult<IReport> =>
  useQuery({
    enabled: !!entityId,
    queryFn: () => getReportByEntity(deviceType, appKey, entityId),
    queryKey: keys.entity(entityId),
  })

export const useSearchComments = ({
  appKey,
  deviceType,
  postId,
  profileId,
}: {
  appKey?: string
  deviceType?: DeviceType
  postId?: string
  profileId?: string
}): UseQueryResult<IReportedComment[]> => {
  return useQuery({
    enabled: !!appKey && !!deviceType && (!!postId || !!profileId),
    queryFn: appKey && deviceType ? () => searchComments({ appKey, deviceType, postId, profileId }) : skipToken,
    queryKey: keys.searchComments({ appKey, deviceType, postId, profileId }),
  })
}

export const useSearchPosts = ({
  appKey,
  deviceType,
  profileId,
}: {
  appKey?: string
  deviceType?: DeviceType
  profileId?: string
}): UseQueryResult<IReportedPost[]> => {
  return useQuery({
    enabled: !!appKey && !!deviceType && !!profileId,
    queryFn: appKey && deviceType ? () => searchPosts({ appKey, deviceType, profileId }) : skipToken,
    queryKey: keys.searchPosts({ appKey, deviceType, profileId }),
  })
}

export const useSearchProjects = ({
  appKey,
  deviceType,
  profileId,
}: {
  appKey?: string
  deviceType?: DeviceType
  profileId?: string
}): UseQueryResult<IProject[]> => {
  return useQuery({
    enabled: !!appKey && !!deviceType && !!profileId,
    queryFn: appKey && deviceType ? () => searchProjects({ appKey, deviceType, profileId }) : skipToken,
    queryKey: keys.searchProjects({ appKey, deviceType, profileId }),
  })
}

////////// MUTATION //////////

export const useProcessBatchReportMutation = (): UseMutationResult<
  void,
  unknown,
  { appKey: string; decision: 'delete' | 'no-action'; deviceType: string; reportIds: string[] },
  unknown
> => {
  return useMutation({ mutationFn: processBatchReport })
}
